import { CountrySpecifics, DimensionUnit, LanguageSpecifics, ProductInfo, ProductInfoType, ProductPropertyStatus } from '@/models/Product'
import { LocationQuery } from 'vue-router'
import { BooleanObject, Modify, Nullable, StringObject } from './GenericInterfaces'
import { Country, Culture, HierarchicalOption, Language } from './Global'
import { BaseProduct } from './Product'
import { DefaultTaxonomicValueType, SimplifiedSuperTaxonomicObject } from './Taxonomy'

export const ImportDeleteMapping = '[[delete]]' as const
export type ImportDeleteMapping = typeof ImportDeleteMapping

export type Importance = 'required' | 'optional' | 'new'

export type ImportStep = 'upload' | 'map' | 'preview'

// Used by preview before import
export interface ProductInput
  extends Partial<Record<ProductInfoType, PreviewProductDetails>>,
    Modify<
      Omit<Partial<BaseProduct>, ProductInfoType>,
      {
        categories?: string[] | null
        tags?: string[] | null
        collections?: string[] | null
        attributes?: AttributeInput[]

        propertyStatuses?: {
          [path: string]: ProductPropertyStatus | null
        }
      }
    > {}

export interface PreviewProductDetails
  extends Modify<
    ProductInfo,
    {
      barcode?: string | number
    }
  > {}

export interface AttributeInput {
  id?: string // should use id first hand if available (attribute may have changed name)
  defaultName: string | number
  delete?: boolean
  options: Array<string | number> | null
  type?: 'existing' | 'new'
  hasTerms?: boolean // if new
  sanitizeValues?: boolean // if new
}

export interface ImportCountrySpecifics
  extends Modify<
    CountrySpecifics,
    {
      basePrice?: string
      recommendedPriceInclVAT?: string
      recommendedPriceExclVAT?: string
      eCommercePrice?: string
      stmPrice?: string
      salePrice?: string
    }
  > {}

export interface ImportLanguageSpecifics extends Modify<LanguageSpecifics, { usps: StringObject }> {}

export type ImportUnitDimensions = Partial<Record<DimensionUnit, string>>

export interface ImportProductInfo
  extends Modify<
    ProductInfo,
    {
      dimensions: ImportUnitDimensions
      weight?: string
      barcode?: string
      isbn?: string
    }
  > {}

export interface ProductImportMappings
  extends Modify<
      Omit<BaseProduct, ProductInfoType>,
      {
        type?: string
        availableInCountries?: string

        countrySpecific: Record<Country, ImportCountrySpecifics>
        languageSpecific: Record<Language, ImportLanguageSpecifics>

        attributes: ProductAttributeMapping[]
        categories: string[]
        tags: string[]
        collections: string[]

        availability: {
          code?: string
          sellableWeb: {
            from?: string
          }
        }
      }
    >,
    Required<Record<ProductInfoType, ImportProductInfo>> {}

export interface ProductImportTemplateInput {
  name: string
  mappings: ProductImportMappings
}

export interface ProductImportTemplate extends ProductImportTemplateInput {
  id: string
}

export interface StagedImportTemplate {
  mappings: ProductImportMappings
  replaceExistingTaxonomies: BooleanObject
  globalAppendMultiValuedAttributes: boolean
}

export interface ProductAttributeMapping {
  column: string // replaced field 260728
  attributeDefaultName?: string
  hasTerms?: boolean
  sanitizeValues?: boolean
  allowMultipleValues?: boolean
  id?: string
}

export interface ParsedImport {
  file: File | undefined
  columns: string[]
  rows: StringObject[]
}

export type ProductEditType = 'script' | 'individual' | 'import'

export interface StoredEditingSession {
  id: string | number
  query: LocationQuery
  dataCulture: Culture | null
  edited: { [sku: string]: ProductInput }
  /** @deprecated */
  includeAllProductsCultures?: boolean
  includedCountries: Country[]
  includeProductsMissingAvailableCountries?: boolean
  date: Date
  checkedOut?: boolean
  name?: string
  stagedOptions?: { [propertyKey: string]: HierarchicalOption<string | number>[] }
}

export interface SkuRenameMapping {
  old: string
  new?: string
}

export interface Modified {
  // [sku: string]: { [propertyName: string]: ModifiedProperty | undefined }
  [sku: string]: { [propertyName: string]: boolean }
}
// export interface ModifiedProperty {
//   delete?: boolean
//   value?: PropertySetterValueTypes
//   status?: ProductPropertyStatus
// }

// export interface Modified2 {
//   [sku: string]: Ref<Set<string>>
// }

export interface ImportResult {
  imported: string[]
  skipped: string[]
  all: string[]
}

export const termsSplitRegex = /\s*,\s*(?![^()]*\))/ // comma separate terms, but don't split terms inside parenthesis

export type ProductInputRecord = { [sku: string]: ProductInput } // used by editing sessions

export const PriceAdjustmentMethods = ['percentage_decrease', 'percentage_to', 'flat_decrease', 'remove'] as const
export type PriceAdjustmentMethod = (typeof PriceAdjustmentMethods)[number]

// export interface TaxonomicObjectInput
//   extends Optional<
//     Modify<
//       SimplifiedSuperTaxonomicObject<string>,
//       {
//         standardOptions?: DefaultTaxonomicValueType[]
//       }
//     >,
//     'id' | 'name'
//   > {}

export interface TaxonomicObjectInput
  extends Nullable<
    Partial<
      Modify<
        SimplifiedSuperTaxonomicObject<string>,
        {
          standardOptions?: DefaultTaxonomicValueType[]
        }
      >
    >
  > {
  // id: string
}
